import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGet, apiPost, errOf } from '../../lib/api';

interface TableRow { id: string; label: string; capacity: number; zone: string; sort_order: number }
interface GuestRow {
  id: string; name: string; company: string; party_size: number; vip: number;
  table_id: string | null; table_label: string | null; reason: string; code: string | null; checked_in_at: string | null;
}
interface TableOcc { tableId: string; label: string; zone: string; capacity: number; seats: number; free: number; checkedIn: number }
interface Report { assigned: number; unassigned: number; unassignedNames: string[]; summary: { tables: TableOcc[]; totalSeats: number; totalCapacity: number; unassignedCount: number; checkedIn: number; invitedSeats: number } }

const field = { width: '100%', borderRadius: 8, padding: '6px 10px', border: '1px solid var(--color-divider)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--font-size-small)' };
const btn = { background: 'var(--color-primary)', color: 'var(--color-on-primary)', border: 'none', cursor: 'pointer' } as const;
const box = { background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' } as const;

/** 管理台「座位」—— 檯／嘉賓（可貼上匯入）／⭐ 自動分檯（**先預覽後落單**）＋ 座位匯總 */
export default function AdminSeatingPage() {
  const { t } = useTranslation();
  const [tables, setTables] = useState<TableRow[]>([]);
  const [guests, setGuests] = useState<GuestRow[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  const [tb, setTb] = useState({ label: '', capacity: '10', zone: 'main' });
  const [gt, setGt] = useState({ name: '', company: '', party_size: '1', vip: false });
  const [paste, setPaste] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      apiGet<{ tables: TableRow[] }>('/api/admin/seating/tables'),
      apiGet<{ guests: GuestRow[] }>('/api/admin/seating/guests'),
    ]);
    if (a.ok && a.data) setTables(a.data.tables);
    if (b.ok && b.data) setGuests(b.data.guests);
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function addTable() {
    if (!tb.label.trim()) return;
    setBusy(true); setErr('');
    const r = await apiPost('/api/admin/seating/tables', { label: tb.label.trim(), capacity: Number(tb.capacity) || 10, zone: tb.zone });
    setBusy(false);
    if (!r.ok) { setErr(errOf(r.data) || t('seat_failed')); return; }
    setTb({ label: '', capacity: '10', zone: 'main' }); setMsg(t('seat_saved')); await load();
  }

  async function addGuest() {
    if (!gt.name.trim()) return;
    setBusy(true); setErr('');
    const r = await apiPost('/api/admin/seating/guests', { name: gt.name.trim(), company: gt.company.trim(), party_size: Number(gt.party_size) || 1, vip: gt.vip ? 1 : 0 });
    setBusy(false);
    if (!r.ok) { setErr(errOf(r.data) || t('seat_failed')); return; }
    setGt({ name: '', company: '', party_size: '1', vip: false }); setMsg(t('seat_saved')); await load();
  }

  async function importGuests() {
    if (!paste.trim()) return;
    setBusy(true); setErr('');
    const r = await apiPost<{ parsed: number; skipped: number }>('/api/admin/seating/guests', { text: paste });
    setBusy(false);
    if (!r.ok) { setErr(errOf(r.data) || t('seat_failed')); return; }
    setMsg(t('seat_parsed', { n: r.data?.parsed ?? 0, skipped: r.data?.skipped ?? 0 }));
    setPaste(''); await load();
  }

  async function assign(commit: boolean) {
    setBusy(true); setErr(''); setMsg('');
    const r = await apiPost<Report>('/api/admin/seating/assign', { commit });
    setBusy(false);
    if (!r.ok || !r.data) { setErr(errOf(r.data) || t('seat_failed')); return; }
    setReport(r.data);
    setMsg(commit ? t('seat_committed', { n: r.data.assigned }) : t('seat_preview'));
    if (commit) await load();
  }

  const checkedIn = guests.filter((g) => g.checked_in_at).length;

  return (
    <div className="max-w-4xl">
      <h2 className="font-bold text-lg mb-1">{t('admin_seating')}</h2>
      <p className="text-xs mb-3" style={{ color: 'var(--color-text-secondary)' }}>{t('seat_intro')}</p>
      {msg && <p className="text-sm mb-2" style={{ color: 'var(--color-primary)' }}>{msg}</p>}
      {err && <p className="text-sm mb-2" style={{ color: 'var(--color-danger)' }}>{err}</p>}

      {/* ── 分檯 ─────────────────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-5" style={box}>
        <h3 className="font-bold mb-2">{t('seat_assign')}</h3>
        <div className="flex flex-wrap gap-2 mb-2">
          <button type="button" disabled={busy} onClick={() => void assign(false)} className="rounded-lg px-4 py-1.5 text-sm font-semibold" style={box}>{t('seat_preview_btn')}</button>
          <button type="button" disabled={busy || !report} onClick={() => void assign(true)} className="rounded-lg px-4 py-1.5 text-sm font-semibold" style={btn}>{t('seat_commit_btn')}</button>
          <button type="button" disabled={busy} onClick={() => void apiPost('/api/admin/seating/assign', { clear: true }).then(load)} className="rounded-lg px-4 py-1.5 text-sm" style={box}>{t('seat_clear')}</button>
        </div>
        <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>{t('seat_rule_note')}</p>
        {report && (
          <div className="text-sm">
            <p>
              {t('seat_result', { assigned: report.assigned, unassigned: report.unassigned })}
              {report.unassignedNames.length > 0 ? ` —— ${report.unassignedNames.join('、')}` : ''}
            </p>
            <ul className="mt-2">
              {report.summary.tables.map((x) => (
                <li key={x.tableId} className="py-0.5">
                  <b>{x.label}</b>（{x.zone}）｜ {t('seat_occupancy', { seats: x.seats, cap: x.capacity })} ｜ {t('seat_free', { n: x.free })}
                </li>
              ))}
            </ul>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              {t('seat_totals', { seats: report.summary.totalSeats, cap: report.summary.totalCapacity, un: report.summary.unassignedCount })}
            </p>
          </div>
        )}
      </section>

      {/* ── 檯 ───────────────────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-5" style={box}>
        <h3 className="font-bold mb-2">
          {t('seat_tables')}（{tables.length}）
        </h3>
        <ul className="text-sm mb-2">
          {tables.length === 0 ? (
            <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
          ) : (
            tables.map((x) => (
              <li key={x.id} className="py-0.5">
                {x.label} ｜ {x.zone} ｜ {t('seat_capacity_n', { n: x.capacity })}
              </li>
            ))
          )}
        </ul>
        <div className="grid grid-cols-3 gap-2">
          <input style={field} placeholder={t('seat_table_label')} value={tb.label} onChange={(e) => setTb({ ...tb, label: e.target.value })} />
          <input style={field} inputMode="numeric" placeholder={t('seat_capacity')} value={tb.capacity} onChange={(e) => setTb({ ...tb, capacity: e.target.value.replace(/[^0-9]/g, '') })} />
          <select style={field} value={tb.zone} onChange={(e) => setTb({ ...tb, zone: e.target.value })}>
            <option value="vip">{t('seat_zone_vip')}</option>
            <option value="main">{t('seat_zone_main')}</option>
            <option value="other">{t('seat_zone_other')}</option>
          </select>
        </div>
        <button type="button" disabled={busy} onClick={() => void addTable()} className="rounded-lg px-4 py-1.5 text-sm font-semibold mt-2" style={btn}>
          {t('seat_table_new')}
        </button>
      </section>

      {/* ── 嘉賓 ─────────────────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-5" style={box}>
        <h3 className="font-bold mb-2">
          {t('seat_guests')}（{guests.length}；{t('seat_checked', { n: checkedIn })}）
        </h3>
        <ul className="text-sm mb-2">
          {guests.length === 0 ? (
            <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
          ) : (
            guests.map((g) => (
              <li key={g.id} className="py-0.5">
                {g.vip ? '⭐ ' : ''}
                <b>{g.name}</b>
                {g.company ? `（${g.company}）` : ''} ｜ {t('seat_party_n', { n: g.party_size })} ｜ {g.table_label ?? t('seat_no_table')}
                {g.checked_in_at ? ` ｜ ✅ ${g.checked_in_at.slice(11, 16)}` : ''}
                {g.reason ? <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}> — {g.reason}</span> : null}
              </li>
            ))
          )}
        </ul>
        <div className="grid grid-cols-4 gap-2 mb-2">
          <input style={field} placeholder={t('f_name')} value={gt.name} onChange={(e) => setGt({ ...gt, name: e.target.value })} />
          <input style={field} placeholder={t('f_company')} value={gt.company} onChange={(e) => setGt({ ...gt, company: e.target.value })} />
          <input style={field} inputMode="numeric" placeholder={t('seat_party')} value={gt.party_size} onChange={(e) => setGt({ ...gt, party_size: e.target.value.replace(/[^0-9]/g, '') })} />
          <label className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={gt.vip} onChange={(e) => setGt({ ...gt, vip: e.target.checked })} /> VIP
          </label>
        </div>
        <button type="button" disabled={busy} onClick={() => void addGuest()} className="rounded-lg px-4 py-1.5 text-sm font-semibold" style={btn}>
          {t('seat_guest_new')}
        </button>
        <textarea style={{ ...field, marginTop: 8 }} rows={4} placeholder={t('seat_paste')} value={paste} onChange={(e) => setPaste(e.target.value)} />
        <button type="button" disabled={busy} onClick={() => void importGuests()} className="rounded-lg px-4 py-1.5 text-sm font-semibold mt-2" style={btn}>
          {t('seat_import')}
        </button>
      </section>
    </div>
  );
}
